import type { SagaReturnType } from 'redux-saga/effects';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { api } from 'src/api';
import { EXPERIMENT_NAMES } from 'src/components/Experiment';
import { getIsHotelOnly } from 'src/store/view/selectors';
import { getIsFeatureHasAnOption } from 'src/utils/featureToggle';

import {
  sortAndTransformFlights,
  transformFlights,
} from '../../utils/flightUtils';

import { ACTUALIZE_PACKAGE_SUCCESS } from '../../../../store/constants/actualization';
import {
  fetchFlightsAction,
  flightsSetAction,
  fligthsFetchingDoneAction,
  origPackageFlightsFetchedAction,
  setBusinessFlights,
  setEconomFlights,
  clearFlightsDuringFiltration,
  changeFlightClassAction,
} from './slice';

import {
  getAlternativeToursIds,
  isAltActualizationDoneSelector,
} from '../actualize-alternatives';
import type { FlightsFilter, PreselectedFilters } from '../flights-filters';
import {
  changeFlightsFilter,
  clearAllFligthsFilters,
  clearFlightsFilter,
  flightsFiltersDataForRequestSelector,
  getFlightsFilterData,
  getHasActiveFilters,
  getUnfilteredFlightsCount,
  isInitialFlightsFetchingDoneSelector,
  preselectedFiltersSelector,
  setAreFetchingFlights,
  setFlightsFilters,
  setIsInitialFlightsFetchingDone,
  setUnfilteredFlightsCount,
} from '../flights-filters';

import { getPackageId } from '../../../../store/selectors/package';

import { getActualizationSuccess } from '../../../../store/selectors/actualization';
import { getIsHomePath } from '../../../../store/selectors/router';
import { packagePriceObjectSelector } from '../../../../store/selectors/tour';
import type { FlightsData } from '../../types/flights';
import { applySingleChoiseFilters } from '../flights-filters/actions';
import { setSpecificFilter } from '../flights-filters/slice';
import { FLIGHT_CLASSES } from './constants';
import {
  businessFlightsCountSelector,
  economyFlightsCountSelector,
  flightsCountSelector,
} from './selectors';
import type { FetchFlightsProps } from './types';

/**
 * Вызываем ручку packages/flights или используем мок перелетов
 */
export function* prepareFlightsFetch() {
  const isHotelOnly = yield select(getIsHotelOnly);
  const packageId = yield select(getPackageId);
  const filterDataForRequest = yield select(
    flightsFiltersDataForRequestSelector,
  );
  const altTourIds = yield select(getAlternativeToursIds);
  const preselectedFilters: PreselectedFilters = yield select(
    preselectedFiltersSelector,
  );
  const unfilteredFlightsCount = yield select(getUnfilteredFlightsCount);

  /**
   * Если юзер выбирал фильтр ТО на отеле, то отправляем только те tour_id,
   * которые соответствуют выбранному оператору до тех пор, пока не получим
   * хотя бы один перелет от них. Таким образом, если юзер выбрал на отеле
   * ТО А и ТО Б и от них не пришло перелетов - вызывется симилар
   */
  const filteredAltTourIds =
    preselectedFilters.operators && !unfilteredFlightsCount
      ? altTourIds.filter((tourId) =>
          // слайсим tour_id, чтобы достать айди оператора (3 первых символа)
          preselectedFilters.operators.includes(Number(tourId.slice(0, 3))),
        )
      : altTourIds;

  const origActualizationSucceed = yield select(getActualizationSuccess);
  const isFirstPage = yield select(getIsHomePath);

  // Убираем перелеты основного пакета, если актуализация еще не прошла или она была неуспешной
  const excludeMain = !origActualizationSucceed;

  const params = {
    ...filterDataForRequest,
    package_id: packageId,
    // Альтернативные перелеты только на первом шаге
    tour_ids: isFirstPage ? filteredAltTourIds : undefined,
    // Исключаем из выдачи перелеты основного пакета, если он сфейлился
    exclude_main: excludeMain || undefined,
  };
  const response = !isHotelOnly
    ? yield api.postPackagesFlights(params)
    : { filters: {}, flights: [] };

  return response;
}

/**
 * Обрабатывает ответ ручки packages/flights и кладет их в стор
 */
export function* fetchFlights(action: FetchFlightsProps) {
  try {
    /*
        Чтобы при актуализации рейсы не стирались была добавлена проверка на action
      */
    if (action.type !== fetchFlightsAction.type) {
      yield put(clearFlightsDuringFiltration());
    }
    yield put(setAreFetchingFlights(true));

    const response = yield call(prepareFlightsFetch);
    const { filters, flights, errors } = response;

    if (errors) {
      throw new Error(errors);
    }

    const hasActiveFilters = yield select(getHasActiveFilters);

    const previousFlightsCount = yield select(flightsCountSelector);

    if (!hasActiveFilters || previousFlightsCount < flights.length) {
      yield put(setUnfilteredFlightsCount(flights.length));
    }

    yield put(setFlightsFilters(filters));

    const packagePriceObject = yield select(packagePriceObjectSelector);

    // группируем флайты для карточек
    const transformedFlights: FlightsData[] = yield call(
      transformFlights,
      flights,
      packagePriceObject,
    );

    const [economFlights, businessFlights]: SagaReturnType<
      typeof sortAndTransformFlights
    > = yield call(sortAndTransformFlights, transformedFlights);

    yield put(setEconomFlights(economFlights));
    yield put(setBusinessFlights(businessFlights));

    // Экшен для отслеживания того, что перелеты попали в стор
    yield put(flightsSetAction());

    const isInitialFlightsFetchingDone = yield select(
      isInitialFlightsFetchingDoneSelector,
    );

    if (!isInitialFlightsFetchingDone) {
      yield put(setIsInitialFlightsFetchingDone(true));
    }

    yield put(setAreFetchingFlights(false));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

export function* handleActualizationSuccess() {
  const isHotelOnly = yield select(getIsHotelOnly);
  if (isHotelOnly) return;
  yield put(fetchFlightsAction());
  yield take(flightsSetAction.type);
  yield put(origPackageFlightsFetchedAction());

  const { NAME, OPTIONS } = EXPERIMENT_NAMES.ACTUALIZE_ALTERNATIVES;
  const allowAlternatives = yield call(
    getIsFeatureHasAnOption,
    NAME,
    OPTIONS.ON,
  );

  const isAltActualizationDone = yield select(isAltActualizationDoneSelector);

  if (!allowAlternatives || isAltActualizationDone) {
    yield put(fligthsFetchingDoneAction());
  }
}

/**
 * Если пришли только бизнес-перелеты, то переключаемся на них
 * Если пришли только эконом перелеты, то переключаемся на них
 */
export function* handleSetFlights() {
  const economyCount = yield select(economyFlightsCountSelector);
  const businessCount = yield select(businessFlightsCountSelector);
  if (!economyCount && businessCount) {
    yield put(changeFlightClassAction(FLIGHT_CLASSES.BUSINESS));
  }
  if (economyCount && !businessCount) {
    yield put(changeFlightClassAction(FLIGHT_CLASSES.ECONOMY));
  }
}

function* singleOptionFilterHandler(
  action: ReturnType<typeof applySingleChoiseFilters>,
) {
  const { path, diff } = action.payload;
  const previousFilters = (yield select(
    getFlightsFilterData,
    path,
  )) as FlightsFilter[];
  const currentFilters = previousFilters.map((filter) =>
    filter.id === diff.id
      ? { ...filter, selected: true }
      : { ...filter, selected: false },
  );
  yield put(setSpecificFilter({ filterName: path, filter: currentFilters }));
  yield call(fetchFlights, action);
}

// eslint-disable-next-line func-names
export default function* () {
  yield takeLatest(
    [
      fetchFlightsAction.type,
      changeFlightsFilter.type,
      clearFlightsFilter.type,
      clearAllFligthsFilters.type,
    ],
    fetchFlights,
  );
  yield takeLatest([applySingleChoiseFilters.type], singleOptionFilterHandler);
  yield takeLatest(ACTUALIZE_PACKAGE_SUCCESS, handleActualizationSuccess);
  yield takeLatest(flightsSetAction.type, handleSetFlights);
}
